const BYTES_PER_KILOBYTE = 1024;
const KILOBYTES_PER_MEGABYTE = 1024;
const MAX_ATTACHMENT_FILE_SIZE_MB = 100;
const MAX_ATTACHMENT_FILE_SIZE_BYTES =
  MAX_ATTACHMENT_FILE_SIZE_MB * KILOBYTES_PER_MEGABYTE * BYTES_PER_KILOBYTE;
const MIN_ATTACHMENT_FILE_SIZE_BYTES = 1;

type AttachmentRejectionReason = 'file-too-large' | 'file-empty' | 'duplicate-file';

interface IRejectedFile {
  file: File;
  reason: AttachmentRejectionReason;
}

interface IAttachmentValidationResult {
  accepted: File[];
  rejected: IRejectedFile[];
}

// name+size+lastModified is a UX de-dupe heuristic only, not a security check
const getFileIdentityKey = (file: File): string =>
  `${file.name}::${file.size}::${file.lastModified}`;

const isFileTooLarge = (file: File): boolean =>
  file.size > MAX_ATTACHMENT_FILE_SIZE_BYTES;

const isFileEmpty = (file: File): boolean => file.size < MIN_ATTACHMENT_FILE_SIZE_BYTES;

function validateSelectedFiles(
  incomingFiles: File[],
  existingFileKeys: string[],
): IAttachmentValidationResult {
  const accepted: File[] = [];
  const rejected: IRejectedFile[] = [];
  const seenKeys = new Set(existingFileKeys);

  incomingFiles.forEach(file => {
    const key = getFileIdentityKey(file);

    if (isFileEmpty(file)) {
      rejected.push({ file, reason: 'file-empty' });
    } else if (isFileTooLarge(file)) {
      rejected.push({ file, reason: 'file-too-large' });
    } else if (seenKeys.has(key)) {
      rejected.push({ file, reason: 'duplicate-file' });
    } else {
      seenKeys.add(key);
      accepted.push(file);
    }
  });

  return { accepted, rejected };
}

export { getFileIdentityKey, MAX_ATTACHMENT_FILE_SIZE_BYTES, validateSelectedFiles };
export type { AttachmentRejectionReason };
